import GuideLayout from '../../layouts/GuideLayout'
import type { TocItem } from '../../layouts/GuideLayout'
import { AffiliateCTA } from '../../components/content/AffiliateCTA'
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed'

const tocItems: TocItem[] = [
  { id: 'migration-trap', label: 'The Premium Downgrade Trap', level: 2 },
  { id: 'cost-drivers', label: 'What Actually Drives Cost', level: 2 },
  { id: 'classic-vs-standard-vs-premium', label: 'Classic vs Standard vs Premium', level: 2 },
  { id: 'waf-ddos', label: 'WAF and DDoS Assumptions', level: 2 },
  { id: 'calculator', label: 'Front Door Calculator', level: 2 },
]

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-700 Designing and Implementing Microsoft Azure Networking Solutions"
    url="https://www.pluralsight.com/paths/az-700-designing-and-implementing-microsoft-azure-networking-solutions"
    price="From £29/month"
    badgeText="Front Door · WAF · CDN"
  />
)

export default function AzureFrontDoorPricingGuide() {
  return (
    <GuideLayout
      title="Azure Front Door Pricing Guide UK 2026"
      description="The expensive Azure Front Door mistake is not traffic — it's choosing Premium, then discovering you can't downgrade to Standard in place. Here's how the pricing model actually breaks down in UK South."
      lastUpdated="April 2026"
      canonical="https://www.azure-calc.co.uk/guides/azure-front-door-pricing-uk"
      metaTitle="Azure Front Door Pricing Guide UK 2026 | AzureCalc.uk"
      metaDescription="Compare Azure Front Door Classic, Standard, and Premium pricing in GBP for UK South, including transfer, WAF, DDoS, and the Premium no-downgrade trap."
      tocItems={tocItems}
      relatedCalculators={[
        { service: 'front-door', path: '/front-door', label: 'Front Door Calculator' },
        { service: 'networking', path: '/networking', label: 'Networking Calculator' },
      ]}
      bottomCta={bottomCta}
    >
      <h2 id="migration-trap">The Premium Downgrade Trap</h2>
      <p>
        The part engineers usually miss is not request pricing. It is the product decision they only discover after a billing review:
        <strong> Azure Front Door Premium cannot be downgraded to Standard in place.</strong> If you choose Premium for a pilot,
        then later decide you only need Standard, the path back is a new profile and a manual migration.
      </p>
      <p>
        That matters because Front Door tends to be deployed early in internet-facing architectures and then left alone. A
        small initial decision can become sticky operational debt: routes, WAF policies, certificates, and DNS cutover all
        have to be revalidated when you migrate later.
      </p>

      <div className="bg-[#e8b000]/10 border border-[#e8b000]/40 p-4 flex gap-3 items-start rounded-sm my-4">
        <span className="material-symbols-outlined text-[#e8b000]">warning</span>
        <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
          <strong className="text-[#e8b000]">Key planning takeaway:</strong> Premium is easy to justify when you need its security
          features now. It is a poor "just in case" default if you may want to move down later.
        </p>
      </div>

      <h2 id="cost-drivers">What Actually Drives Cost</h2>
      <p>
        Front Door pricing looks simple at first, but four separate dimensions usually show up in real architectures:
      </p>
      <ul>
        <li><strong>Base profile fee:</strong> a standing cost for the selected tier</li>
        <li><strong>Requests:</strong> charged by request volume, which matters for chatty web apps and API-heavy traffic</li>
        <li><strong>Outbound transfer:</strong> edge-to-user traffic, which can exceed the request line item for media-heavy sites</li>
        <li><strong>Security features:</strong> WAF and DDoS assumptions, which can dominate low-traffic environments</li>
      </ul>
      <p>
        The hidden optimisation is often architectural: reducing outbound bytes, collapsing unnecessary rules, and choosing the
        lowest tier that matches your actual feature set beats obsessing over tiny per-request differences.
      </p>

      <h2 id="classic-vs-standard-vs-premium">Classic vs Standard vs Premium</h2>
      <p>
        <strong>Classic</strong> is a retirement problem. If you still run it, the comparison you need is not "what does Classic cost?"
        but "what is the least painful migration target?"
      </p>
      <p>
        <strong>Standard</strong> is usually the right baseline when you need modern Front Door capabilities without paying for Premium-only
        security posture. <strong>Premium</strong> earns its keep when the advanced security and edge features are truly used — not just enabled.
      </p>
      <p>
        In practical terms, Standard is the default recommendation for teams that want modern Front Door without creating future
        migration friction.
      </p>

      <h2 id="waf-ddos">WAF and DDoS Assumptions</h2>
      <p>
        Security pricing is where teams start mixing unrelated controls. Front Door WAF, Front Door's built-in platform protections,
        and Azure DDoS Protection are not interchangeable cost buckets. This guide treats them separately on purpose.
      </p>
      <p>
        The calculator's first-pass model is designed for estimation, not invoice reconstruction. It is most useful for comparing
        tiers and for surfacing how easily security add-ons can distort a "cheap CDN" assumption.
      </p>

      <h2 id="calculator">Front Door Calculator</h2>
      <p>
        Use the calculator below to compare Classic, Standard, and Premium using the same request, transfer, WAF, and DDoS inputs.
        The warning state is intentional: this is a migration decision tool as much as a pricing tool.
      </p>

      <div className="my-6">
        <CalculatorEmbed service="front-door" />
      </div>
    </GuideLayout>
  )
}
